import { useState, useMemo, useCallback, useRef, KeyboardEvent } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Button,
  ClickAwayListener,
  Popper,
  Paper,
  SxProps,
  Theme,
} from '@mui/material';

import { SelectableItem, MultiSelectSearchListProps } from '../types';

/**
 * MultiSelectSearchList - A multi-select component with search and grouping
 *
 * Features:
 * - Real-time search filtering
 * - Items grouped by category with collapsible headers
 * - Selected items displayed as removable chips
 * - Keyboard accessible
 *
 * @example
 * ```tsx
 * const [selected, setSelected] = useState<string[]>([]);
 *
 * <MultiSelectSearchList
 *   items={items}
 *   selectedItems={selected}
 *   onSelectionChange={setSelected}
 *   placeholder="Search majors..."
 * />
 * ```
 */
export const MultiSelectSearchList = ({
  items,
  selectedItems,
  onSelectionChange,
  placeholder = 'Search...',
  maxSelections,
  emptyMessage = 'No items found',
  renderItem,
}: MultiSelectSearchListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();

    return items.filter(
      (item) => item.name.toLowerCase().includes(query) || item.group.toLowerCase().includes(query),
    );
  }, [items, searchQuery]);

  // Group items by their group property
  const groupedItems = useMemo(() => {
    const groups: Record<string, SelectableItem[]> = {};

    filteredItems.forEach((item) => {
      if (!groups[item.group]) {
        groups[item.group] = [];
      }

      groups[item.group].push(item);
    });

    // Sort groups alphabetically
    const sortedGroups = Object.keys(groups).sort();

    // Sort items within each group alphabetically
    return sortedGroups.map((group) => ({
      group,
      items: groups[group].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [filteredItems]);

  const handleToggleItem = useCallback(
    (itemName: string) => {
      const isSelected = selectedItems.includes(itemName);

      if (isSelected) {
        // Remove from selection
        const newSelection = selectedItems.filter((name) => name !== itemName);

        onSelectionChange(newSelection);
      } else {
        // Add to selection (if under max limit)
        if (maxSelections && selectedItems.length >= maxSelections) {
          return;
        }

        onSelectionChange([...selectedItems, itemName]);
      }
    },
    [selectedItems, onSelectionChange, maxSelections],
  );

  const handleRemoveChip = useCallback(
    (itemName: string) => {
      const newSelection = selectedItems.filter((name) => name !== itemName);

      onSelectionChange(newSelection);
    },
    [selectedItems, onSelectionChange],
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setSearchQuery('');
      setIsDropdownOpen(false);
    }
  }, []);

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setIsDropdownOpen(false);
    // Only call onSelectionChange - the container's handleSelectionChange
    // already clears the primary when selections are empty.
    // Calling both causes a race condition with stale searchParams.
    onSelectionChange([]);
  }, [onSelectionChange]);

  const handleClickAway = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleListItemKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, itemName: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggleItem(itemName);
      }
    },
    [handleToggleItem],
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={containerSx}>
        {/* Search Row with External Buttons */}
        <Box ref={anchorRef} sx={searchRowSx}>
          <Box sx={searchInputContainerSx}>
            <TextField
              id="multi-select-search-input"
              fullWidth
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              inputProps={{
                autoComplete: 'off',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={searchIconSx} />
                  </InputAdornment>
                ),
              }}
              sx={searchFieldSx}
            />
          </Box>
          <Button
            id="multi-select-clear-button"
            variant="outlined"
            type="button"
            onClick={handleClear}
            sx={clearButtonSx}
            aria-label="Clear search"
          >
            Clear
          </Button>
          <Button
            id="multi-select-search-button"
            variant="contained"
            onClick={() => setIsDropdownOpen(true)}
            sx={searchButtonSx}
            aria-label="Search"
          >
            Search
          </Button>
        </Box>

        {/* Dropdown Popover */}
        <Popper
          open={isDropdownOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <Paper sx={dropdownPaperSx}>
            {filteredItems.length === 0 ? (
              <Typography variant="body2" sx={emptyMessageSx}>
                {emptyMessage}
              </Typography>
            ) : (
              <List dense disablePadding>
                {groupedItems.map(({ group, items: groupItems }) => (
                  <Box key={group}>
                    {/* Group Header */}
                    <Typography variant="subtitle2" sx={groupHeaderSx}>
                      {group} ({groupItems.length})
                    </Typography>

                    {/* Group Items */}
                    {groupItems.map((item) => {
                      const isSelected = selectedItems.includes(item.name);
                      const isDisabled =
                        !isSelected &&
                        maxSelections !== undefined &&
                        selectedItems.length >= maxSelections;

                      if (renderItem) {
                        return (
                          <Box
                            key={item.name}
                            onClick={() => !isDisabled && handleToggleItem(item.name)}
                          >
                            {renderItem(item, isSelected)}
                          </Box>
                        );
                      }

                      return (
                        <ListItem key={item.name} disablePadding>
                          <ListItemButton
                            onClick={() => handleToggleItem(item.name)}
                            onKeyDown={(e) => handleListItemKeyDown(e, item.name)}
                            disabled={isDisabled}
                            disableRipple
                            disableTouchRipple
                            sx={getListItemButtonSx}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <ListItemIcon sx={checkboxContainerSx}>
                              <Box sx={checkboxIconContainerSx(isSelected)}>
                                <AppIcon
                                  iconName={isSelected ? 'Check' : 'Square'}
                                  diameter="16px"
                                  color={
                                    isSelected
                                      ? 'Colors/Foreground/fg-white'
                                      : 'Colors/Foreground/fg-primary'
                                  }
                                />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              primaryTypographyProps={{
                                variant: 'text-md-medium',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Popper>

        {/* Selected Items Badges */}
        {selectedItems.length > 0 && (
          <Box sx={selectedBadgesContainerSx}>
            {selectedItems.map((name) => (
              <Box key={name} sx={selectedBadgeSx}>
                <Typography sx={badgeLabelSx}>{name}</Typography>
                <IconButton
                  id={`multi-select-remove-${name.toLowerCase().replace(/\s+/g, '-')}-button`}
                  size="small"
                  onClick={() => handleRemoveChip(name)}
                  aria-label={`Remove ${name}`}
                  sx={badgeCloseButtonSx}
                >
                  <CloseIcon sx={badgeCloseIconSx} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

// Styles

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
};

const searchRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  width: '100%',
};

const searchInputContainerSx: SxProps<Theme> = {
  flex: 1,
};

const searchFieldSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: backrs.colors['Colors/Base/White'],
    borderRadius: backrs.radius['radius-md'],
    '& fieldset': {
      borderColor: backrs.colors['Colors/Base/Black'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: backrs.colors['Colors/Base/Black'],
    },
    '&.Mui-focused fieldset': {
      borderColor: backrs.colors['Colors/Base/Black'],
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: backrs.colors['Gray/500'],
    opacity: 1,
  },
});

const searchIconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/Black'],
});

const clearButtonSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-semibold'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Gray/300']}`,
  borderRadius: backrs.radius['radius-md'],
  color: backrs.colors['Gray/700'],
  textTransform: 'none',
  px: 2,
  py: '10px', // Hardcoded: specific button padding from Figma
  minWidth: 'auto',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)', // Hardcoded: subtle shadow not in token system
  '&:hover': {
    backgroundColor: backrs.colors['Gray/50'],
    borderColor: backrs.colors['Gray/300'],
  },
});

const searchButtonSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-semibold'],
  backgroundColor: backrs.colors['Brand/500'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-md'],
  color: backrs.colors['Colors/Base/White'],
  textTransform: 'none',
  px: 2,
  py: '10px', // Hardcoded: specific button padding from Figma
  minWidth: 'auto',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)', // Hardcoded: subtle shadow not in token system
  '&:hover': {
    backgroundColor: backrs.colors['Brand/600'],
  },
});

const dropdownPaperSx: SxProps<Theme> = ({ backrs }) => ({
  maxHeight: 400,
  overflowY: 'auto',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  boxShadow: backrs.shadows['shadow-xl'],
  mt: backrs.spacing['spacing-md'],
  width: 'fit-content',
  minWidth: backrs.width['width-xxs'],
  maxWidth: backrs.width['width-xs'],
});

const emptyMessageSx: SxProps<Theme> = ({ backrs }) => ({
  textAlign: 'center',
  py: 4,
  color: backrs.colors['Colors/Foreground/fg-tertiary'],
});

const groupHeaderSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-medium'],
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  textTransform: 'capitalize',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)', // Hardcoded: subtle lift shadow for sticky header
  position: 'sticky',
  top: 0,
  zIndex: 1,
});

const getListItemButtonSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-sm'],
  px: backrs.spacing['spacing-lg'],
  '&:hover': {
    backgroundColor: `${backrs.colors['Colors/Base/Black']}0A`, // 4% opacity
  },
});

const checkboxContainerSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: backrs.spacing['spacing-4xl'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const checkboxIconContainerSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
    flexShrink: 0,
  });

// Selected badges styling (pill-shaped badges below input)
const selectedBadgesContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 1.5,
};

const selectedBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  borderRadius: backrs.radius['radius-full'],
  pl: 1.5,
  pr: 0.5,
  py: 0.5,
  gap: 0.25,
});

const badgeLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-medium'],
  color: backrs.colors['Brand/700'],
});

const badgeCloseButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: '2px', // Hardcoded: minimal padding for close button
  '&:hover': {
    backgroundColor: `${backrs.colors['Brand/700']}1A`, // 10% opacity
  },
});

const badgeCloseIconSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/400'],
  fontSize: 12, // Hardcoded: small icon size for badge close
});
